// FAQ Page
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronDown } from "lucide-react";
import Navigation from "../components/shared/Navigation";
import Footer from "../components/shared/Footer";
import PageBreadcrumb from "../components/shared/PageBreadcrumb";
import { usePageMeta, generateFAQSchema, SchemaScript } from "../components/shared/SEOHelper";

const FAQPage = () => {
  usePageMeta({
    title: "FAQ | Yoga Questions Answered | Annelie Lily Yoga Frome",
    description: "Frequently asked questions about yoga classes, treatments, booking, and what to expect at yoga with Annelie Lily in Frome, Somerset.",
    canonical: "https://annelielily.com/faq"
  });

  const faqCategories = [
    {
      title: "Getting Started",
      faqs: [
        {
          question: "I've never done yoga before — is that okay?",
          answer: "Absolutely. Many of my students started as complete beginners. Classes are small enough that I can give you individual attention, and I always offer easier options for every pose. You don't need to be flexible, fit, or have any experience at all."
        },
        {
          question: "What should I wear to yoga?",
          answer: "Comfortable clothing that allows you to move freely. Nothing too loose (it can get in the way) and nothing too tight (you need to breathe). Yoga is practiced barefoot, so no special footwear needed."
        },
        {
          question: "What should I bring?",
          answer: "A yoga mat if you have one (I have spares). A water bottle. Maybe a blanket or jumper for relaxation — you can get cold lying still. Avoid eating a heavy meal right before class."
        },
        {
          question: "How do I book a class?",
          answer: "Just get in touch via the contact form, email, or phone. I'll let you know what's available and help you find a class that suits your schedule and experience level. No pressure, no hard sell."
        }
      ]
    },
    {
      title: "Classes & Sessions",
      faqs: [
        {
          question: "What's the difference between Hatha and Yin yoga?",
          answer: "Hatha is more active — we move between poses, build strength, work the muscles. Yin is passive — we hold poses for 3-5 minutes, targeting deeper connective tissue. Both have their place. Many people do both."
        },
        {
          question: "How many people are in a class?",
          answer: "Maximum 8 people. Small enough that I can see everyone and offer individual guidance, but big enough that you're not on your own."
        },
        {
          question: "How often should I come to yoga?",
          answer: "Once a week is a good start — you'll feel the benefits and it's sustainable. Some people build up to 2-3 times weekly as yoga becomes part of their routine. There's no right answer; whatever you can manage consistently is better than nothing."
        },
        {
          question: "Do you offer private sessions?",
          answer: "Yes. One-to-one sessions are available for those who prefer individual attention, have specific needs, or want to work on something particular. Private sessions can be scheduled at times that work for you."
        }
      ]
    },
    {
      title: "Health & Safety",
      faqs: [
        {
          question: "I have an injury / health condition — can I still do yoga?",
          answer: "Usually, yes — but let me know what's going on before class. Many conditions can be worked with safely using appropriate modifications. If I'm not sure, I'll be honest and might suggest a private session first or consulting your doctor."
        },
        {
          question: "Is yoga safe during pregnancy?",
          answer: "Yoga can be wonderful during pregnancy, but it needs to be adapted. Let me know if you're pregnant so I can offer appropriate modifications. For prenatal-specific work, private sessions are often best."
        },
        {
          question: "I'm not flexible — will I be able to do the poses?",
          answer: "Yes. Flexibility isn't a prerequisite for yoga — it's something that develops over time. I offer easier versions of every pose, and we use props to make things accessible. You work where your body is today."
        }
      ]
    },
    {
      title: "Treatments",
      faqs: [
        {
          question: "What treatments do you offer?",
          answer: "Swedish massage for physical tension, Reiki for energy work, chakra healing for working with energy centres, crystal healing, and AuraTransformation. Get in touch and I can discuss which might be most helpful for what you're experiencing."
        },
        {
          question: "Do I need to believe in energy healing for it to work?",
          answer: "An open mind is helpful, but you don't need specific beliefs. Many people approach Reiki and chakra work with curiosity rather than conviction and still find it deeply relaxing and beneficial."
        },
        {
          question: "How do I know which treatment is right for me?",
          answer: "Get in touch and tell me what you're hoping to address. Generally: massage for physical tension, Reiki for relaxation and energy balance, chakra healing for deeper energy work. I'm happy to advise."
        }
      ]
    },
    {
      title: "Practical Matters",
      faqs: [
        {
          question: "How much do classes cost?",
          answer: "Prices vary depending on the type of session. Get in touch and I'll send you current rates. I offer drop-in classes, block bookings (better value), and private session packages."
        },
        {
          question: "Where are you based?",
          answer: "Little Keyford Lane, Frome, Somerset BA11 5BA. About 15-25 minutes from Bath, Trowbridge, Shepton Mallet, Warminster, Wells, and Radstock."
        },
        {
          question: "Is there parking?",
          answer: "Yes, parking is available nearby. I'll send you directions when you book."
        },
        {
          question: "What's your cancellation policy?",
          answer: "I ask for 24 hours notice if you need to cancel. Life happens — but if I can fill your spot, everyone benefits."
        }
      ]
    }
  ];

  // Flatten all FAQs for schema
  const allFaqs = faqCategories.flatMap(cat => cat.faqs);
  const faqSchema = generateFAQSchema(allFaqs);

  const [openItems, setOpenItems] = useState({});

  const toggleItem = (catIndex, faqIndex) => {
    const key = `${catIndex}-${faqIndex}`;
    setOpenItems(prev => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div className="min-h-screen bg-cream">
      <SchemaScript schema={faqSchema} />
      <Navigation />
      <PageBreadcrumb currentPage="FAQ" currentSlug="faq" />
      
      {/* Hero */}
      <section className="pt-8 pb-12 px-6 md:px-12 bg-muted">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-xs uppercase tracking-[0.2em] text-gold font-semibold mb-3 block">
              Help & Information
            </span>
            <h1 className="font-heading text-4xl md:text-5xl font-light text-neutral-800 mb-5">
              Frequently Asked Questions
            </h1>
            <p className="text-neutral-700 leading-relaxed max-w-2xl mx-auto">
              Everything you might want to know before your first class or treatment. 
              If your question isn't here, just ask.
            </p>
          </motion.div>
        </div>
      </section>
      
      {/* FAQ Categories */}
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-3xl mx-auto space-y-12">
          {faqCategories.map((category, catIndex) => (
            <motion.div
              key={catIndex}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: catIndex * 0.05 }}
            >
              <h2 className="font-heading text-2xl text-neutral-800 mb-4">{category.title}</h2>
              <div className="space-y-3">
                {category.faqs.map((faq, faqIndex) => {
                  const key = `${catIndex}-${faqIndex}`;
                  const isOpen = openItems[key];
                  
                  return (
                    <div key={faqIndex} className="bg-white/70 rounded-xl border border-gold/10 overflow-hidden">
                      <button
                        onClick={() => toggleItem(catIndex, faqIndex)}
                        className="w-full p-5 text-left flex items-center justify-between gap-4"
                      >
                        <h3 className="font-heading text-lg text-neutral-800">{faq.question}</h3>
                        <ChevronDown className={`w-5 h-5 text-gold flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                      </button>
                      {isOpen && (
                        <div className="px-5 pb-5">
                          <p className="text-neutral-600 text-sm">{faq.answer}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </motion.div>
          ))}
        </div>
      </section>
      
      {/* Still Have Questions */}
      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-4">
              Still Have Questions?
            </h2>
            <p className="text-neutral-600 mb-6">
              If you can't find what you're looking for, just get in touch. 
              I'm happy to chat through anything you're unsure about.
            </p>
            <Link to="/contact" className="btn-primary">
              Get in Touch <ArrowRight className="w-4 h-4 inline ml-2" />
            </Link>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FAQPage;
